/* istanbul ignore file */
/* eslint-disable max-lines */
import _ from 'lodash';
import { Flex } from 'components/base/layout-components';
import Text from 'components/base/Text';
import { SPACES } from 'common/constants/sizes';
import {
  TRACKING,
  SEARCH_DATE_FORMAT,
  DAYS_PARAM
} from 'common/constants/constants';
import * as TrackingService from 'common/services/tracking.service';
import GoalCol from './components/GoalCol';
import TrackingCol from './components/TrackingCol';
import ActionsCol from './components/ActionsCol';
import NotificationsCol from './components/NotificationsCol';
import DashboardCol from './components/DashboardCol';
import {
  pullRequestsLargerThan,
  pullRequestsOpenMoreThan,
  workAtRiskNotification,
  mergedWithoutReviewNotification,
  mergedWithBasicReviewNotification,
  reviewRequestHangingNotification,
  reviewTooLongNotification,
  dailyDigestNotificationThresholds,
  NOTIFICATION_ENABLED_SETTING_KEYS,
  NOTIFICATION_THRESHOLDS_TO_OMIT_KEYS
} from './notifications.config';
import { toggleDashboardWidget } from 'common/services/metrics.service';
import moment from 'moment';
import { pluralize } from 'common/utils/utils';

export const formatReviewTimeDaysAndHours = reviewTime => {
  if (reviewTime === 0) {
    return '0 hours';
  }

  const days = _.floor(reviewTime / 24, 0);
  const hours = reviewTime % 24;

  let result = '';
  result += days ? `${pluralize(days, 'day')}` : '';
  if (days > 0 && hours > 0) {
    result += ', ';
  }
  result += hours ? `${pluralize(hours, 'hour')}` : '';

  return result;
};

export function formatDaysToDaysAndHours(days) {
  const hours = days ? _.round(days * 24, 0) : 0;
  return formatReviewTimeDaysAndHours(hours);
}

export const formatReviewTime = reviewTime => {
  return _.round(reviewTime / 24, 1);
};

export const formatReviewTimeUnit = reviewTime => {
  return formatReviewTime(reviewTime) === 1 ? 'day' : 'days';
};

export const notificationPredictionType = {
  BRANCHES: 'branches',
  PRS: 'prs',
  ISSUES: 'issues'
};

export const getGoals = () => ({
  ...pullRequestsLargerThan(),
  ...reviewRequestHangingNotification(),
  ...reviewTooLongNotification(),
  ...pullRequestsOpenMoreThan(),
  ...mergedWithoutReviewNotification(),
  ...mergedWithBasicReviewNotification(),
  ...workAtRiskNotification(),
  ...dailyDigestNotificationThresholds()
});

export const IconToggleTooltip = (title, content) => {
  return (
    <Flex direction='column' padding='small'>
      <Text size='medium' weight='500'>
        {title}
      </Text>
      <Text pt={SPACES.small}>{content}</Text>
    </Flex>
  );
};

export const toggleWidgetsForTeamDashboard = (
  teamDashboard,
  setTeamDashboard
) => {
  const { teamDashboardId, teamDashboardWidgets = [] } = teamDashboard;

  return _.debounce(
    async (enabled, widgetId, notificationId, notificationKey, name) => {
      const originTeamDashboard = teamDashboard;
      try {
        const updatedWidgets = enabled
          ? [...teamDashboardWidgets, widgetId]
          : teamDashboardWidgets.filter(w => w !== widgetId);

        const updatedTeamDashboard = {
          ...teamDashboard,
          teamDashboardWidgets: updatedWidgets
        };

        setTeamDashboard(updatedTeamDashboard);

        TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
          page: TRACKING.PAGES.SETTINGS,
          page_tab: TRACKING.PAGE_TABS.NOTIFICATIONS,
          click_type: enabled
            ? TRACKING.CLICK.TEAM_DASHBOARD_WIDGET_ADDED
            : TRACKING.CLICK.TEAM_DASHBOARD_WIDGET_REMOVED,
          name
        });

        await toggleDashboardWidget(
          teamDashboardId,
          enabled ? 'add' : 'remove',
          widgetId
        ).catch(() => {
          setTeamDashboard(originTeamDashboard);
        });
      } catch (e) {
        return false;
      }
    },
    500
  );
};

export const toggleNotificationsForTeam = (
  notifications,
  setNotifications,
  NOTIFICATIONS_BUILDER,
  onSaveSetting,
  selectedTeamId
) => {
  return _.debounce(
    async (value, enabledTypeKey, notificationId, notificationKey, name) => {
      // NOTIFICATIONS_BUILDER holds which enabled key to set for each IconToggle click.
      const enabledKey = NOTIFICATIONS_BUILDER[notificationKey][enabledTypeKey];

      try {
        const updatedNotifications = notifications.map(n =>
          n.id === notificationId
            ? {
                ...n,
                // remove enabled: value when opening showNewNotificationsSetting FF
                values: { ...n.values, [enabledKey]: value, enabled: value }
              }
            : n
        );

        const notification = _.find(
          updatedNotifications,
          ({ id }) => id === notificationId
        );

        TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
          page: TRACKING.PAGES.SETTINGS,
          page_tab: TRACKING.PAGE_TABS.NOTIFICATIONS,
          click_type: value
            ? TRACKING.CLICK.SLACK_OPT_IN
            : TRACKING.CLICK.SLACK_OPT_OUT,
          name
        });

        setNotifications(updatedNotifications);

        await onSaveSetting(
          notificationKey,
          {
            ...notification.values
          },
          'team',
          selectedTeamId
        );
      } catch (e) {
        return false;
      }
    },
    500
  );
};

export const getTableData = (
  notifications,
  teamDashboard,
  setNotifications,
  setTeamDashboard,
  teamDashboardWidgets,
  NOTIFICATIONS_BUILDER,
  onSaveSetting,
  getTeamOrOrgSetting,
  selectedTeamId,
  history,
  toggleModal,
  toggleSnackBar,
  teamDashboardId,
  highlightWidgetId,
  predictionCountByKey = {}, // predictionCount
  prMerged,
  setPredictionCount
) => {
  const shouldShowEstimation = (headsUp, violation) => !!headsUp || !!violation;
  const shouldDoubleEstimation = (headsUp, violation) =>
    !!headsUp && !!violation;

  return !notifications.length
    ? false
    : notifications.map(
        ({
          id,
          icon,
          articleId,
          notificationKey,
          values,
          isNew,
          isHeadsUp,
          widgetId
        }) => {
          const { name, chartName } = NOTIFICATIONS_BUILDER[notificationKey];

          // Clicking on the violation/warning IconToggle updates scheme.
          // NOTIFICATIONS_BUILDER holds which enabled key to set for each Notification.
          // for example: clicking NOTICIATION iconToggle for Daily digest sets enabled {"enabled": true/false};
          //              clicking NOTICIATION iconToggle for other notifications sets violation_enabled {"violation_enabled": true/false};

          const {
            violationEnabledKey,
            warningEnabledKey,
            similarNotifications
          } = NOTIFICATIONS_BUILDER[notificationKey];

          const description =
            NOTIFICATIONS_BUILDER[notificationKey].description(values);
          const tooltip = NOTIFICATIONS_BUILDER[notificationKey].tooltip
            ? NOTIFICATIONS_BUILDER[notificationKey].tooltip(values)
            : null;
          const headsUpTooltip = NOTIFICATIONS_BUILDER[notificationKey]
            .headsUpTooltip
            ? NOTIFICATIONS_BUILDER[notificationKey].headsUpTooltip(values)
            : null;
          const isNotificationEnabled = values[violationEnabledKey];

          const isHeadsUpEnabled = values[warningEnabledKey];

          const isWidgetInTeamDashboard =
            teamDashboardWidgets.includes(widgetId);

          const isShowEstimation = shouldShowEstimation(
            isHeadsUpEnabled,
            isNotificationEnabled
          );
          const isDoubleEstimation = shouldDoubleEstimation(
            isHeadsUpEnabled,
            isNotificationEnabled
          );
          const predictionCount = _.get(
            _.get(predictionCountByKey, `${notificationKey}`),
            'count'
          );
          const estimateNumber = isDoubleEstimation
            ? predictionCount * 2
            : predictionCount;

          const { withEstimation = true, getInModalDescription } =
            NOTIFICATIONS_BUILDER[notificationKey];
          const inModalDescription = getInModalDescription
            ? getInModalDescription({
                getTeamOrOrgSetting,
                isOrgSettings: false,
                selectedTeamId,
                history,
                toggleModal
              })
            : null;
          return {
            styles: { highlight: highlightWidgetId === widgetId },
            goal: (
              <GoalCol
                name={name}
                chartName={chartName}
                isNew={isNew}
                widgetId={widgetId}
              />
            ),
            tracking: (
              <TrackingCol
                description={description}
                estimateNumber={estimateNumber}
                isShowEstimation={isShowEstimation}
              />
            ),
            actions: (
              <ActionsCol
                inModalDescription={inModalDescription}
                withEstimation={withEstimation}
                toggleModal={toggleModal}
                toggleSnackBar={toggleSnackBar}
                selectedTeamId={selectedTeamId}
                id={id}
                icon={icon}
                name={name}
                description={description}
                articleId={articleId}
                notifications={notifications}
                setNotifications={setNotifications}
                onSaveSetting={onSaveSetting}
                NOTIFICATION_CONFIG={_.get(
                  NOTIFICATIONS_BUILDER,
                  notificationKey,
                  {}
                )}
                NOTIFICATIONS_BUILDER={NOTIFICATIONS_BUILDER}
                prMerged={prMerged}
                predictionCount={predictionCountByKey}
                setPredictionCount={setPredictionCount}
              />
            ),
            notifications: (
              <NotificationsCol
                isHeadsUp={isHeadsUp}
                toggleNotificationsForTeam={toggleNotificationsForTeam(
                  notifications,
                  setNotifications,
                  NOTIFICATIONS_BUILDER,
                  onSaveSetting,
                  selectedTeamId
                )}
                id={id}
                notificationKey={notificationKey}
                name={name}
                tooltip={tooltip}
                headsUpTooltip={headsUpTooltip}
                notificationEnabled={isNotificationEnabled}
                headsUpEnabled={isHeadsUpEnabled}
                similarNotifications={similarNotifications}
                notifications={notifications}
                toggleModal={toggleModal}
                notificationsConfig={NOTIFICATIONS_BUILDER}
              />
            ),
            ...(teamDashboardId &&
              widgetId && {
                dashboard: (
                  <DashboardCol
                    widgetId={widgetId}
                    widgetEnabled={isWidgetInTeamDashboard}
                    toggleWidgetsForTeamDashboard={toggleWidgetsForTeamDashboard(
                      teamDashboard,
                      setTeamDashboard
                    )}
                    id={id}
                    notificationKey={notificationKey}
                    name={name}
                  />
                )
              })
          };
        }
      );
};

export const getTableColumns = teamDashboardId => {
  return [
    {
      name: 'GOAL',
      dataPoint: 'goal',
      tableCellProps: {
        style: { width: '25rem', borderLeft: 0 }
      }
    },
    {
      name: 'SETTINGS',
      dataPoint: 'tracking',
      tableCellProps: {
        style: { borderRight: 0, textAlign: 'right !important' }
      }
    },
    {
      name: '',
      dataPoint: 'actions',
      tableCellProps: { style: { width: '15rem', borderLeft: 0 } }
    },
    {
      name: 'NOTIFICATIONS',
      dataPoint: 'notifications',
      align: 'center',
      tableCellProps: {
        style: { width: '8rem', borderRight: 0 }
      }
    },
    teamDashboardId && {
      name: 'DASHBOARD',
      dataPoint: 'dashboard',
      align: 'center',
      tableCellProps: { style: { borderLeft: 0 } }
    }
  ].filter(Boolean);
};

export const getNotificationThresholds = notification =>
  _.omit(
    _.get(notification, 'values'),
    _.map(NOTIFICATION_ENABLED_SETTING_KEYS).concat(
      _.map(NOTIFICATION_THRESHOLDS_TO_OMIT_KEYS)
    )
  );

// minimum orgs prs merged required
export const PRS_THRESHHOLD = 30;

export const TWO_WEEKS_IN_DAYS = '14';

export const today = moment().format(SEARCH_DATE_FORMAT);
export const twoWeeksAgo = moment()
  .subtract(TWO_WEEKS_IN_DAYS, DAYS_PARAM)
  .format(SEARCH_DATE_FORMAT);

export const mergedPrReq = [
  {
    is_draft: false,
    updated_at: { before: today, after: twoWeeksAgo },
    state: ['merged']
  }
];

export const getNotificationsByType = (
  notifications,
  NOTIFICATIONS_BUILDER,
  type
) => {
  return notifications
    .map(notification => {
      const notificationKey = _.get(notification, 'notificationKey');
      const notificationDefinition = _.get(
        NOTIFICATIONS_BUILDER,
        notificationKey
      );

      const notificationDefinitionType = _.get(
        notificationDefinition.prediction({}),
        '[0].type'
      );
      if (notificationDefinitionType === type) return notification;
    })
    .filter(Boolean);
};

export const overPrsThreshholdCheck = (prMerged, PRS_THRESHHOLD) =>
  prMerged.length > 0 && _.get(prMerged, '[0].count') >= PRS_THRESHHOLD;

export const addVerboseReviewTimeIfNeeded = reviewTime => {
  return reviewTime > 24 ? `(${formatReviewTimeDaysAndHours(reviewTime)})` : '';
};

export const formatVerboseReviewTime = review_time => {
  return `${pluralize(review_time, 'hour')} ${addVerboseReviewTimeIfNeeded(
    review_time
  )}`;
};
